import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { DealershipChooser } from 'components';
import {
  Form,
  Select,
  InputNumber,
  Button,
  Card,
  Col,
  Row,
  Spin,
  notification,
} from 'antd';
import styled from 'styled-components';
import { observe } from 'mobx';
import { ShopStore } from 'stores';
import { get } from 'lodash';
import dayjs from 'dayjs';
import { FiltersFormWithVVA } from 'components/FleetReports/FiltersFormWithVVA';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { downloadCSV } from 'shared/utils';
import { notifications } from '../../helpers/notificationHelper';
import { ExportOutlined } from '@ant-design/icons';

const { Option } = Select;

const H2Styled = styled.h2`
  font-size: 1.2rem;
`;

class FleetCostsByVmrsPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
  };

  state = {
    isLoadingPartCost: false,
    isLoadingReasonsForVisits: false,
    rawData: [],
    dataForReasonsForVisits: [],
    dataForPartCost: [],
    dateRange: [dayjs().subtract(30, 'day'), dayjs()],
    limit: 20,
    filter: {
      vendor: [],
      make: [],
      model: [],
      year: [],
      carIds: [],
      assetType: [],
      VMRSSysCode: [],
    },
  };

  componentDidMount() {
    this.init();
  }

  componentWillUnmount() {
    this.disposer();
  }

  disposer = observe(ShopStore.currentShop, 'id', () => {
    this.init();
  });

  deburrLoadFleetCosts = null;

  loadDataForPartCost = async () => {
    try {
      this.setState({ isLoadingPartCost: true });
      const { limit, filter, dateRange } = this.state;
      let [startDate, endDate] = dateRange;
      const response = await ShopStore.getShopDataForVmrsCosts(
        ShopStore.currentShop.id,
        limit,
        filter,
        startDate,
        endDate
      );
      const data = get(response, 'data', []);

      this.setState({ rawData: data });

      // Process the data
      const processedData = [];
      const xAxisCategories = data.map((item) => item.vmrsDescription);
      data.forEach((item, index) => {
        processedData.push({
          x: index,
          y: item.totalCost,
          part_name: item.vmrsDescription,
        });
      });

      this.setState({
        dataForPartCost: processedData,
        part_names: xAxisCategories,
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message,
      });
      this.setState({ dataForPartCost: [] });
    } finally {
      this.setState({ isLoadingPartCost: false });
    }
  };

  init = async () => {
    this.loadDataForPartCost();
  };

  handleFilterChange = (changedFields) => {
    this.setState(changedFields);

    // call deburrLoadAssetDuration
    if (changedFields.limit || changedFields.dateRange) {
      if (this.deburrLoadFleetCosts) {
        clearTimeout(this.deburrLoadFleetCosts);
      }
      this.deburrLoadFleetCosts = setTimeout(() => {
        this.loadDataForPartCost();
      }, 500);
    }
  };

  handleResetFilters = () => {
    this.setState(
      {
        filter: {
          vendor: [],
          make: [],
          model: [],
          year: [],
          carIds: [],
          assetType: [],
          VMRSSysCode: [],
        },
        dateRange: [dayjs().subtract(30, 'day'), dayjs()],
        limit: 20,
      },
      () => {
        this.loadDataForPartCost();
      }
    );
  };

  handleApplyFilters = () => {
    this.init();
  };

  handleExportToCSV = async () => {
    // Show a loading notification
    const notificationId = notifications.loading(
      'Exporting VMRS Cost data to CSV... You can keep using the app while this is happening.'
    );

    try {
      const { rawData } = this.state;

      const csvHeaders = ['VMRS System Code', 'Part Description', 'Total Cost'];

      const csvData = rawData.map((item) => [
        item.vmrsCode,
        item.vmrsDescription,
        item.totalCost,
      ]);

      const fileName = `VMRS Costs Data - ${new Date().toLocaleDateString()}`;
      downloadCSV([csvHeaders, ...csvData], fileName, false);

      // Update notification to success
      notifications.success('VMRS Cost data exported successfully!', {
        id: notificationId,
      });
    } catch (error) {
      // Update notification to error
      const errorMessage = error?.message || 'Unknown error occurred';
      notifications.error(`Failed to export CSV: ${errorMessage}`, {
        id: notificationId,
      });
    }
  };

  render() {
    const { limit, filter, dateRange, isLoadingPartCost } = this.state;

    const CostByVmrsChartOptions = {
      chart: {
        type: 'column',
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: this.state.part_names,
        crosshair: true,
        title: {
          text: 'VMRS Description',
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Cost',
        },
      },
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (this.y < 1000 && this.y > 0) {
                return Math.round(this.y); // Display numbers less than 1000 rounded to the nearest whole number
              } else if (this.y === 0) {
                return null;
              } else {
                return Math.round(this.y / 100) / 10 + 'k'; // Display 1000 and greater rounded to the nearest hundred with 'k'
              }
            },
            style: {
              textOutline: '2px contrast',
            },
          },
        },

        series: {
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: [
        {
          name: 'Cost',
          data: this.state.dataForPartCost.map((item) => ({
            y: item.y,
            part_name: item.part_name,
          })),
          color: '#183cbc',
        },
      ],
    };

    return (
      <Fragment>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <h1>Fleet Costs By VMRS System</h1>
          </Col>
        </Row>
        <DealershipChooser />
        <FiltersFormWithVVA
          filter={{
            filter: filter,
          }}
          dateRange={dateRange}
          limit={limit}
          hideStatusFilter={true}
          onChange={this.handleFilterChange}
          onReset={this.handleResetFilters}
          onApply={this.handleApplyFilters}
        />
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Spin spinning={isLoadingPartCost}>
              <Card>
                <Row gutter={[8, 8]}>
                  <Col span={24}>
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={CostByVmrsChartOptions}
                    />
                  </Col>
                </Row>
              </Card>
            </Spin>
          </Col>
        </Row>
      </Fragment>
    );
  }
}

export default withRouter(observer(FleetCostsByVmrsPage));
